import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Paper, Typography, Grid, Chip, Divider,
  Tab, Tabs, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, CircularProgress, Avatar, TextField,
  Autocomplete, Card, CardContent, Accordion, AccordionSummary,
  AccordionDetails, Alert, IconButton, Tooltip, Button,
  Dialog, DialogTitle, DialogContent,
} from '@mui/material';
import {
  Person, Phone, Bloodtype, CalendarMonth, LocalHospital,
  Assignment, Receipt, MedicalServices, Biotech, WorkspacePremium,
  ExpandMore, OpenInNew, FiberManualRecord, Male, Female,
  MonitorHeart, Scale, AirlineSeatReclineNormal, Thermostat,
} from '@mui/icons-material';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as ReTooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  patientService, prescriptionService, appointmentService,
  billingService, issuedCertificateService,
} from '../services/api';
import TopBar from '../components/TopBar';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '');

const fmt = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};
const fmtMoney = (n) => '₹ ' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const calcAge = (dob) => {
  if (!dob) return '—';
  const diff = Date.now() - new Date(dob).getTime();
  return Math.floor(diff / (365.25 * 24 * 60 * 60 * 1000)) + ' yrs';
};

// ── Stat card ─────────────────────────────────────────────────────────────────
const StatCard = ({ icon, label, value, color }) => (
  <Paper elevation={2} sx={{ p: 2, borderRadius: 2, borderLeft: `4px solid ${color}`, height: '100%' }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Box>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, color, mt: 0.5 }}>{value}</Typography>
      </Box>
      <Box sx={{ color, opacity: 0.5, fontSize: 40 }}>{icon}</Box>
    </Box>
  </Paper>
);

// ── Vital badge ───────────────────────────────────────────────────────────────
const VitalBadge = ({ icon, label, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 2 }}>
    <Box sx={{ color: '#546e7a', display: 'flex' }}>{icon}</Box>
    <Typography variant="caption" color="text.secondary">{label}:</Typography>
    <Typography variant="caption" fontWeight={600}>{value || '—'}</Typography>
  </Box>
);

// ── Section heading ───────────────────────────────────────────────────────────
const SectionHead = ({ children }) => (
  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, color: '#1565c0',
    borderBottom: '2px solid #e3f2fd', pb: 0.5 }}>
    {children}
  </Typography>
);

export default function Patient360Page() {
  const navigate = useNavigate();
  const [searchOptions, setSearchOptions] = useState([]);
  const [searching,     setSearching]     = useState(false);
  const [patient,       setPatient]       = useState(null);
  const [loading,       setLoading]       = useState(false);
  const [tab,           setTab]           = useState(0);
  const searchTimer = useRef(null);

  // Data buckets
  const [prescriptions,  setPrescriptions]  = useState([]);
  const [appointments,   setAppointments]   = useState([]);
  const [bills,          setBills]          = useState([]);
  const [labReports,     setLabReports]     = useState([]);
  const [certificates,   setCertificates]   = useState([]);
  const [viewingReport,    setViewingReport]    = useState(null);
  const [reportViewerOpen, setReportViewerOpen] = useState(false);

  const handleSearchInput = useCallback((_, inputVal) => {
    clearTimeout(searchTimer.current);
    if (!inputVal || inputVal.length < 2) { setSearchOptions([]); return; }
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await patientService.searchPatients(inputVal);
        setSearchOptions(res.data);
      } catch {
        setSearchOptions([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  }, []);

  const loadPatient = useCallback(async (p) => {
    if (!p) { setPatient(null); return; }
    setPatient(p);
    setTab(0);
    setLoading(true);
    try {
      const [rxRes, apptRes, billRes, labRes, certRes] = await Promise.allSettled([
        patientService.getPatientHistory(p.id),
        appointmentService.getAppointmentsByPatient(p.id),
        billingService.getBillsByPatient(p.id),
        patientService.getLabReports(p.id),
        issuedCertificateService.getAll({ patientId: p.id }),
      ]);
      setPrescriptions(rxRes.status  === 'fulfilled' ? rxRes.value.data  : []);
      setAppointments(apptRes.status === 'fulfilled' ? apptRes.value.data : []);
      setBills(billRes.status        === 'fulfilled' ? billRes.value.data : []);
      setLabReports(labRes.status    === 'fulfilled' ? labRes.value.data  : []);
      setCertificates(certRes.status === 'fulfilled' ? certRes.value.data : []);
    } finally {
      setLoading(false);
    }
  }, []);

  // Field helpers — backend now returns bp/pulse/weight/spo2 (camelCase of Bp/Pulse/Weight/Spo2)
  const getBP     = (rx) => rx.bp     || rx.bP     || rx.BP     || '';
  const getSPO2   = (rx) => rx.spo2   || rx.sPO2   || rx.SPO2   || '';
  const getPulse  = (rx) => rx.pulse  || rx.Pulse  || '';
  const getWeight = (rx) => rx.weight || rx.Weight || '';

  const parseSystolic  = (bp) => { const p = bp?.split('/'); return p?.length ? parseFloat(p[0]) || null : null; };
  const parseDiastolic = (bp) => { const p = bp?.split('/'); return p?.length >= 2 ? parseFloat(p[1]) || null : null; };

  // Vitals chart data — one point per prescription (oldest → newest for left-to-right)
  const vitalsData = [...prescriptions]
    .reverse()
    .filter(rx => getBP(rx) || getPulse(rx) || getWeight(rx) || getSPO2(rx))
    .map(rx => ({
      date:      fmt(rx.date || rx.Date),
      systolic:  parseSystolic(getBP(rx)),
      diastolic: parseDiastolic(getBP(rx)),
      pulse:     getPulse(rx)  ? parseFloat(getPulse(rx))  || null : null,
      weight:    getWeight(rx) ? parseFloat(getWeight(rx)) || null : null,
      spo2:      getSPO2(rx)   ? parseFloat(getSPO2(rx))   || null : null,
    }));

  const totalBillAmount = bills.reduce((s, b) => s + (b.netAmount || 0), 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <TopBar title="Patient 360°" onBack={() => navigate('/dashboard')} />

      <Container maxWidth="xl" sx={{ py: 3 }}>

        {/* ── Search ── */}
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1565c0' }}>
            Search Patient
          </Typography>
          <Autocomplete
            options={searchOptions}
            filterOptions={(x) => x}
            loading={searching}
            getOptionLabel={(p) =>
              `${p.firstName} ${p.lastName}${p.phoneNumber ? ' — ' + p.phoneNumber : ''}`
            }
            onInputChange={handleSearchInput}
            onChange={(_, val) => loadPatient(val)}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Type at least 2 characters to search…"
                variant="outlined"
                size="medium"
                sx={{ maxWidth: 520 }}
              />
            )}
            renderOption={(props, p) => (
              <Box component="li" {...props} sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                <Avatar sx={{ width: 34, height: 34, bgcolor: p.gender === 'Female' ? '#e91e63' : '#1565c0', fontSize: 14 }}>
                  {p.firstName?.charAt(0)}
                </Avatar>
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {p.firstName} {p.lastName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {calcAge(p.dateOfBirth)} • {p.gender} • {p.phoneNumber || 'No phone'}
                  </Typography>
                </Box>
              </Box>
            )}
          />
        </Paper>

        {/* ── No patient selected ── */}
        {!patient && (
          <Paper elevation={2} sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
            <Person sx={{ fontSize: 72, color: '#bdbdbd', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Search and select a patient to view their 360° profile
            </Typography>
          </Paper>
        )}

        {/* ── Loading ── */}
        {patient && loading && (
          <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress size={48} /></Box>
        )}

        {/* ── Patient Profile ── */}
        {patient && !loading && (
          <>
            {/* ── Demographics Header ── */}
            <Paper elevation={3} sx={{ p: 3, borderRadius: 2, mb: 3,
              background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)', color: '#fff' }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item>
                  <Avatar sx={{
                    width: 72, height: 72, fontSize: 28, fontWeight: 700,
                    bgcolor: patient.gender === 'Female' ? '#e91e63' : '#42a5f5',
                    border: '3px solid rgba(255,255,255,0.4)',
                  }}>
                    {patient.firstName?.charAt(0)}
                  </Avatar>
                </Grid>
                <Grid item xs>
                  <Typography variant="h5" fontWeight={700}>
                    {patient.firstName} {patient.lastName}
                    {patient.gender === 'Female'
                      ? <Female sx={{ ml: 1, fontSize: 20, verticalAlign: 'middle' }} />
                      : <Male sx={{ ml: 1, fontSize: 20, verticalAlign: 'middle' }} />}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 0.5, opacity: 0.9 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CalendarMonth sx={{ fontSize: 16 }} />
                      <Typography variant="body2">{calcAge(patient.dateOfBirth)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Phone sx={{ fontSize: 16 }} />
                      <Typography variant="body2">{patient.phoneNumber || '—'}</Typography>
                    </Box>
                    {patient.bloodGroup && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Bloodtype sx={{ fontSize: 16 }} />
                        <Typography variant="body2">{patient.bloodGroup}</Typography>
                      </Box>
                    )}
                    {patient.address && (
                      <Typography variant="body2" sx={{ opacity: 0.85 }}>{patient.address}</Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                    {patient.hindiPrescription   && <Chip label="Hindi Rx"   size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff' }} />}
                    {patient.marathiPrescription && <Chip label="Marathi Rx" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff' }} />}
                    {patient.lmp && <Chip label={`LMP: ${fmt(patient.lmp)}`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff' }} />}
                  </Box>
                </Grid>

                {/* Quick stats inline */}
                <Grid item xs={12} sm="auto">
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    {[
                      { label: 'Visits',       value: appointments.length,  },
                      { label: 'Prescriptions', value: prescriptions.length, },
                      { label: 'Bills',         value: bills.length,         },
                      { label: 'Lab Reports',   value: labReports.length,    },
                    ].map(s => (
                      <Box key={s.label} sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" fontWeight={700}>{s.value}</Typography>
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>{s.label}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {/* ── KPI Cards ── */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} sm={3}>
                <StatCard icon={<LocalHospital />}   label="Total Appointments" value={appointments.length}  color="#1565c0" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatCard icon={<Assignment />}       label="Prescriptions"      value={prescriptions.length} color="#2e7d32" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatCard icon={<Receipt />}          label="Total Billed"       value={fmtMoney(totalBillAmount)} color="#e65100" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatCard icon={<WorkspacePremium />} label="Certificates"       value={certificates.length}  color="#6a1b9a" />
              </Grid>
            </Grid>

            {/* ── Tabs ── */}
            <Paper elevation={2} sx={{ borderRadius: 2 }}>
              <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
              >
                <Tab label="Prescriptions" />
                <Tab label="Vitals History" />
                <Tab label="Appointments" />
                <Tab label="Lab Reports" />
                <Tab label="Certificates" />
                <Tab label="Bills" />
                <Tab label="Demographics" />
              </Tabs>

              <Box sx={{ p: 3 }}>

                {/* ── Tab 0: Prescriptions ── */}
                {tab === 0 && (
                  prescriptions.length === 0
                    ? <Alert severity="info">No prescriptions found for this patient.</Alert>
                    : prescriptions.map((rx, i) => (
                      <Accordion key={rx.id} defaultExpanded={i === 0}
                        sx={{ mb: 1, borderRadius: '8px !important', '&:before': { display: 'none' } }}
                        elevation={1}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', flexWrap: 'wrap' }}>
                            <Chip
                              label={fmt(rx.date)}
                              size="small"
                              color="primary"
                              variant="outlined"
                              icon={<CalendarMonth sx={{ fontSize: '14px !important' }} />}
                            />
                            {rx.chiefComplaint && (
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#37474f' }}>
                                {rx.chiefComplaint}
                              </Typography>
                            )}
                            {rx.provisionalDiagnosis && (
                              <Chip label={rx.provisionalDiagnosis} size="small" color="warning" variant="outlined" />
                            )}
                            <Box sx={{ display: 'flex', ml: 'auto', flexWrap: 'wrap' }}>
                              <VitalBadge icon={<MonitorHeart sx={{ fontSize: 14 }} />}             label="BP"    value={getBP(rx)     || '—'} />
                              <VitalBadge icon={<AirlineSeatReclineNormal sx={{ fontSize: 14 }} />} label="Pulse" value={getPulse(rx)  || '—'} />
                              <VitalBadge icon={<Scale sx={{ fontSize: 14 }} />}                    label="Wt"    value={getWeight(rx) ? `${getWeight(rx)} kg` : '—'} />
                              <VitalBadge icon={<Thermostat sx={{ fontSize: 14 }} />}               label="SpO₂"  value={getSPO2(rx)   ? `${getSPO2(rx)}%` : '—'} />
                            </Box>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0 }}>
                          <Grid container spacing={2}>
                            {rx.prescriptionMedicines?.length > 0 && (
                              <Grid item xs={12} md={6}>
                                <SectionHead>Medicines ({rx.prescriptionMedicines.length})</SectionHead>
                                {rx.prescriptionMedicines.map((pm) => (
                                  <Box key={pm.id} sx={{ display: 'flex', gap: 1, mb: 0.5, alignItems: 'flex-start' }}>
                                    <FiberManualRecord sx={{ fontSize: 8, mt: 0.8, color: '#1565c0', flexShrink: 0 }} />
                                    <Box>
                                      <Typography variant="body2" fontWeight={600}>{pm.medicine?.name || '—'}</Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {[pm.dosage, pm.duration].filter(Boolean).join(' · ')}
                                      </Typography>
                                    </Box>
                                  </Box>
                                ))}
                              </Grid>
                            )}
                            {rx.prescriptionAdvices?.length > 0 && (
                              <Grid item xs={12} md={6}>
                                <SectionHead>Advice</SectionHead>
                                {rx.prescriptionAdvices.map((pa) => (
                                  <Box key={pa.id} sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
                                    <FiberManualRecord sx={{ fontSize: 8, mt: 0.8, color: '#2e7d32', flexShrink: 0 }} />
                                    <Typography variant="body2">{pa.advice?.text || '—'}</Typography>
                                  </Box>
                                ))}
                              </Grid>
                            )}
                            {rx.prescriptionInvestigations?.length > 0 && (
                              <Grid item xs={12} md={6}>
                                <SectionHead>Investigations</SectionHead>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                  {rx.prescriptionInvestigations.map((pi) => (
                                    <Chip key={pi.id} label={pi.investigation?.name} size="small"
                                      color="secondary" variant="outlined" />
                                  ))}
                                </Box>
                              </Grid>
                            )}
                            {rx.followUpDate && (
                              <Grid item xs={12} md={6}>
                                <SectionHead>Follow Up</SectionHead>
                                <Chip label={`Follow up: ${fmt(rx.followUpDate)}`} color="warning" size="small" />
                              </Grid>
                            )}
                            {rx.referDoctorName && (
                              <Grid item xs={12} md={6}>
                                <SectionHead>Referred To</SectionHead>
                                <Typography variant="body2">
                                  {/^dr\.?\s/i.test(rx.referDoctorName) ? rx.referDoctorName : `Dr. ${rx.referDoctorName}`}
                                </Typography>
                              </Grid>
                            )}
                            {(rx.medicalNotes || rx.MedicalNotes) && (
                              <Grid item xs={12}>
                                <SectionHead>Medical Notes</SectionHead>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: '#37474f' }}>
                                  {rx.medicalNotes || rx.MedicalNotes}
                                </Typography>
                              </Grid>
                            )}
                            {rx.allergy && (
                              <Grid item xs={12} md={6}>
                                <SectionHead>Allergy</SectionHead>
                                <Chip label={rx.allergy || rx.Allergy} size="small" color="error" variant="outlined" />
                              </Grid>
                            )}
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    ))
                )}

                {/* ── Tab 1: Vitals History ── */}
                {tab === 1 && (
                  vitalsData.length < 2
                    ? <Alert severity="info">Not enough vitals data to plot charts (need at least 2 records).</Alert>
                    : <Grid container spacing={3}>

                      {/* BP — dual line: Systolic + Diastolic */}
                      <Grid item xs={12} md={6}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                          <Typography variant="subtitle2" fontWeight={700} mb={1}>
                            Blood Pressure (mmHg)
                          </Typography>
                          <ResponsiveContainer width="100%" height={220}>
                            <LineChart data={vitalsData}
                              margin={{ top: 15, right: 15, left: -10, bottom: 5 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                              <YAxis tick={{ fontSize: 11 }} domain={['auto', 'auto']} />
                              <ReTooltip
                                formatter={(v, name) => [`${v} mmHg`, name]}
                                itemSorter={(item) => item.dataKey === 'systolic' ? 0 : 1}
                              />
                              <Legend />
                              <Line type="monotone" dataKey="systolic"  name="Systolic"
                                stroke="#e53935" strokeWidth={2} dot={{ r: 4 }} connectNulls />
                              <Line type="monotone" dataKey="diastolic" name="Diastolic"
                                stroke="#fb8c00" strokeWidth={2} dot={{ r: 4 }} connectNulls
                                strokeDasharray="5 3" />
                            </LineChart>
                          </ResponsiveContainer>
                        </Paper>
                      </Grid>

                      {/* Pulse */}
                      <Grid item xs={12} md={6}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                          <Typography variant="subtitle2" fontWeight={700} mb={1}>Pulse (bpm)</Typography>
                          <ResponsiveContainer width="100%" height={220}>
                            <LineChart data={vitalsData}
                              margin={{ top: 15, right: 15, left: -10, bottom: 5 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                              <YAxis tick={{ fontSize: 11 }} domain={['auto', 'auto']} />
                              <ReTooltip formatter={(v) => [`${v} bpm`, 'Pulse']} />
                              <Line type="monotone" dataKey="pulse" name="Pulse"
                                stroke="#1e88e5" strokeWidth={2} dot={{ r: 4 }} connectNulls />
                            </LineChart>
                          </ResponsiveContainer>
                        </Paper>
                      </Grid>

                      {/* Weight */}
                      <Grid item xs={12} md={6}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                          <Typography variant="subtitle2" fontWeight={700} mb={1}>Weight (kg)</Typography>
                          <ResponsiveContainer width="100%" height={220}>
                            <LineChart data={vitalsData}
                              margin={{ top: 15, right: 15, left: -10, bottom: 5 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                              <YAxis tick={{ fontSize: 11 }} domain={['auto', 'auto']} />
                              <ReTooltip formatter={(v) => [`${v} kg`, 'Weight']} />
                              <Line type="monotone" dataKey="weight" name="Weight"
                                stroke="#43a047" strokeWidth={2} dot={{ r: 4 }} connectNulls />
                            </LineChart>
                          </ResponsiveContainer>
                        </Paper>
                      </Grid>

                      {/* SpO2 */}
                      <Grid item xs={12} md={6}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                          <Typography variant="subtitle2" fontWeight={700} mb={1}>SpO₂ (%)</Typography>
                          <ResponsiveContainer width="100%" height={220}>
                            <LineChart data={vitalsData}
                              margin={{ top: 15, right: 15, left: -10, bottom: 5 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                              <YAxis tick={{ fontSize: 11 }} domain={['auto', 'auto']} />
                              <ReTooltip formatter={(v) => [`${v}%`, 'SpO₂']} />
                              <Line type="monotone" dataKey="spo2" name="SpO₂"
                                stroke="#8e24aa" strokeWidth={2} dot={{ r: 4 }} connectNulls />
                            </LineChart>
                          </ResponsiveContainer>
                        </Paper>
                      </Grid>

                    </Grid>
                )}

                {/* ── Tab 2: Appointments ── */}
                {tab === 2 && (
                  appointments.length === 0
                    ? <Alert severity="info">No appointments found.</Alert>
                    : <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                            <TableCell><b>Date</b></TableCell>
                            <TableCell><b>Type</b></TableCell>
                            <TableCell><b>Status</b></TableCell>
                            <TableCell><b>BP</b></TableCell>
                            <TableCell><b>Pulse</b></TableCell>
                            <TableCell><b>Weight</b></TableCell>
                            <TableCell><b>SpO₂</b></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {[...appointments].sort((a, b) => new Date(b.appointmentDate) - new Date(a.appointmentDate))
                            .map((a) => (
                            <TableRow key={a.id} hover>
                              <TableCell>{fmt(a.appointmentDate)}</TableCell>
                              <TableCell>
                                <Chip label={a.type} size="small"
                                  color={a.type === 'IPD' ? 'secondary' : 'primary'} variant="outlined" />
                              </TableCell>
                              <TableCell>
                                <Chip label={a.status} size="small"
                                  color={a.status === 'Completed' ? 'success' : a.status === 'Cancelled' ? 'error' : 'warning'}
                                  variant="filled" />
                              </TableCell>
                              <TableCell>{a.bp || '—'}</TableCell>
                              <TableCell>{a.pulse || '—'}</TableCell>
                              <TableCell>{a.weight ? `${a.weight} kg` : '—'}</TableCell>
                              <TableCell>{a.spO2 || a.spo2 || '—'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                )}

                {/* ── Tab 3: Lab Reports ── */}
                {tab === 3 && (
                  labReports.length === 0
                    ? <Alert severity="info">No lab reports uploaded for this patient.</Alert>
                    : <Grid container spacing={2}>
                      {labReports.map((lr) => (
                        <Grid item xs={12} sm={6} md={4} key={lr.id}>
                          <Card
                            variant="outlined"
                            sx={{ borderRadius: 2, cursor: 'pointer', '&:hover': { boxShadow: 3, borderColor: 'primary.main' } }}
                            onClick={() => { setViewingReport(lr); setReportViewerOpen(true); }}
                          >
                            <CardContent sx={{ pb: '12px !important' }}>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                <Biotech sx={{ color: '#1565c0', mt: 0.3 }} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                                    {lr.reportType || lr.fileName}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    {lr.fileName}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {fmt(lr.uploadedAt)}{lr.labName ? ` · ${lr.labName}` : ''}
                                  </Typography>
                                </Box>
                                <OpenInNew fontSize="small" sx={{ color: '#90a4ae', flexShrink: 0, mt: 0.3 }} />
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                )}

                {/* ── Tab 4: Certificates ── */}
                {tab === 4 && (
                  certificates.length === 0
                    ? <Alert severity="info">No certificates issued to this patient.</Alert>
                    : <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                            <TableCell><b>Cert No</b></TableCell>
                            <TableCell><b>Type</b></TableCell>
                            <TableCell><b>Doctor</b></TableCell>
                            <TableCell><b>Date</b></TableCell>
                            <TableCell><b>Issued At</b></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {certificates.map((c) => (
                            <TableRow key={c.id} hover>
                              <TableCell><Chip label={c.certificateNo} size="small" variant="outlined" /></TableCell>
                              <TableCell>{c.certificateTypeName || c.certificateType}</TableCell>
                              <TableCell>{c.doctorName || '—'}</TableCell>
                              <TableCell>{fmt(c.certDate)}</TableCell>
                              <TableCell>{fmt(c.issuedAt)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                )}

                {/* ── Tab 5: Bills ── */}
                {tab === 5 && (
                  bills.length === 0
                    ? <Alert severity="info">No bills generated for this patient.</Alert>
                    : <>
                      <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
                        <Paper sx={{ p: 1.5, px: 2.5, borderRadius: 2, bgcolor: '#e8f5e9', border: '1px solid #a5d6a7' }}>
                          <Typography variant="caption" color="text.secondary">Total Billed</Typography>
                          <Typography variant="h6" fontWeight={700} color="#2e7d32">{fmtMoney(totalBillAmount)}</Typography>
                        </Paper>
                        <Paper sx={{ p: 1.5, px: 2.5, borderRadius: 2, bgcolor: '#e3f2fd', border: '1px solid #90caf9' }}>
                          <Typography variant="caption" color="text.secondary">Total Bills</Typography>
                          <Typography variant="h6" fontWeight={700} color="#1565c0">{bills.length}</Typography>
                        </Paper>
                      </Box>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                              <TableCell><b>Date</b></TableCell>
                              <TableCell><b>Payment Mode</b></TableCell>
                              <TableCell align="right"><b>Subtotal</b></TableCell>
                              <TableCell align="right"><b>Discount</b></TableCell>
                              <TableCell align="right"><b>Tax</b></TableCell>
                              <TableCell align="right"><b>Net Amount</b></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {[...bills].sort((a, b) => new Date(b.billDate) - new Date(a.billDate))
                              .map((b) => (
                              <TableRow key={b.id} hover>
                                <TableCell>{fmt(b.billDate)}</TableCell>
                                <TableCell>
                                  <Chip label={b.paymentMode} size="small"
                                    color={b.paymentMode === 'Cash' ? 'success' : b.paymentMode === 'Card' ? 'primary' : 'warning'}
                                    variant="outlined" />
                                </TableCell>
                                <TableCell align="right">{fmtMoney(b.subtotal)}</TableCell>
                                <TableCell align="right" sx={{ color: '#e65100' }}>
                                  {b.discountAmount > 0 ? `- ${fmtMoney(b.discountAmount)}` : '—'}
                                </TableCell>
                                <TableCell align="right">
                                  {(b.cgstAmount + b.sgstAmount) > 0 ? fmtMoney(b.cgstAmount + b.sgstAmount) : '—'}
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, color: '#1565c0' }}>
                                  {fmtMoney(b.netAmount)}
                                </TableCell>
                              </TableRow>
                            ))}
                            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                              <TableCell colSpan={5} align="right" sx={{ fontWeight: 700 }}>Grand Total</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, color: '#2e7d32', fontSize: '1rem' }}>
                                {fmtMoney(totalBillAmount)}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                )}

                {/* ── Tab 6: Demographics ── */}
                {tab === 6 && (
                  <Grid container spacing={3}>
                    {[
                      { label: 'First Name',         value: patient.firstName },
                      { label: 'Last Name',          value: patient.lastName },
                      { label: 'Date of Birth',      value: fmt(patient.dateOfBirth) },
                      { label: 'Age',                value: calcAge(patient.dateOfBirth) },
                      { label: 'Gender',             value: patient.gender },
                      { label: 'Blood Group',        value: patient.bloodGroup || '—' },
                      { label: 'Phone Number',       value: patient.phoneNumber || '—' },
                      { label: 'Email',              value: patient.email || '—' },
                      { label: 'Address',            value: patient.address || '—' },
                      { label: 'Emergency Contact',  value: patient.emergencyContact || '—' },
                      { label: 'LMP',                value: patient.lmp ? fmt(patient.lmp) : '—' },
                      { label: 'EDD',                value: patient.edd ? fmt(patient.edd) : '—' },
                      { label: 'Hindi Prescription', value: patient.hindiPrescription ? 'Yes' : 'No' },
                      { label: 'Marathi Prescription', value: patient.marathiPrescription ? 'Yes' : 'No' },
                    ].map(({ label, value }) => (
                      <Grid item xs={12} sm={6} md={4} key={label}>
                        <Box sx={{ borderBottom: '1px solid #eeeeee', pb: 1 }}>
                          <Typography variant="caption" color="text.secondary">{label}</Typography>
                          <Typography variant="body1" fontWeight={500} sx={{ mt: 0.2 }}>{value}</Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                )}

              </Box>
            </Paper>
          </>
        )}
      </Container>

      {/* Inline Lab Report Viewer */}
      <Dialog
        open={reportViewerOpen}
        onClose={() => setReportViewerOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: '90vh' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Biotech sx={{ color: '#1565c0' }} />
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>{viewingReport?.reportType || 'Lab Report'}</Typography>
              <Typography variant="caption" color="text.secondary">{viewingReport?.fileName}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              startIcon={<OpenInNew />}
              onClick={() => window.open(`${API_BASE}/${(viewingReport?.fileUrl || '').replace(/\\/g, '/')}`, '_blank')}
            >
              Open in new tab
            </Button>
            <Button size="small" onClick={() => setReportViewerOpen(false)}>Close</Button>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          {viewingReport && (() => {
            const url = `${API_BASE}/${viewingReport.fileUrl.replace(/\\/g, '/')}`;
            const isImage = /\.(jpg|jpeg|png|bmp|webp|tiff?)$/i.test(viewingReport.fileName);
            return isImage ? (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'auto', p: 2 }}>
                <img src={url} alt={viewingReport.fileName} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              </Box>
            ) : (
              <iframe src={url} title={viewingReport.fileName} style={{ flex: 1, width: '100%', height: '100%', border: 'none' }} />
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
